import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { dashboardApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { CompanyOverviewPanel } from '@/components/dashboard/CompanyOverviewPanel';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { DashboardSkeleton } from '@/components/ui/Skeleton';
import { PageSection } from '@/components/ui/PageSection';
import { Callout } from '@/components/ui/Callout';
import { useAppSelector } from '@/hooks/useAppSelector';
import { formatCurrency } from '@/utils/format';
import type { OrgStoreSummary } from '@/types/dashboard';
import { showApiError } from '@/utils/apiError';

export function OrgDashboardPage() {
  const location = useLocation();
  const isPosDashboard = location.pathname.startsWith('/pos');
  const storeId = useAppSelector((s) => s.app.selectedStore?.id);
  const [storeCards, setStoreCards] = useState<OrgStoreSummary[]>([]);

  const { data: stats, isLoading: loading } = useQuery({
    queryKey: ['org-dashboard-stats', storeId],
    queryFn: async () => {
      try {
        return await dashboardApi.orgStats(storeId);
      } catch (err) {
        showApiError(err, 'Failed to load dashboard');
        throw err;
      }
    },
    enabled: !!storeId,
  });

  useEffect(() => {
    dashboardApi
      .storesSummary()
      .then((res) => setStoreCards(res.stores))
      .catch(() => {});
  }, []);

  const selectedCurrency = useAppSelector((s) => s.app.selectedStore?.currencyCode) ?? 'INR';

  const pageTitle = isPosDashboard ? 'POS Dashboard' : 'Organization Dashboard';
  const pageDescription = isPosDashboard
    ? 'Store sales, inventory, and point-of-sale operations'
    : 'Company-wide KPIs and per-store financial overview';

  if (loading && storeId) {
    return (
      <div className="page-content">
        <PageHeader title={pageTitle} description={pageDescription} />
        <DashboardSkeleton kpiCount={6} />
      </div>
    );
  }

  return (
    <div className="page-content">
      <PageHeader title={pageTitle} description={pageDescription} />

      {!isPosDashboard ? <CompanyOverviewPanel /> : null}

      {storeCards.length > 0 && (
        <PageSection title="Sales by store" description="Each store shown in its own currency — never merged">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {storeCards.map((s) => (
              <div key={s.storeId} className="card p-5">
                <p className="font-semibold text-slate-900 dark:text-slate-100">{s.name}</p>
                <p className="text-xs text-slate-500">{s.currencyCode}</p>
                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-slate-500">Today</p>
                    <p className="font-semibold tabular-nums">{formatCurrency(s.todaySales, s.currencyCode)}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">This month</p>
                    <p className="font-semibold tabular-nums">{formatCurrency(s.monthSales, s.currencyCode)}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-500">Invoices today</p>
                    <p className="font-semibold tabular-nums">{s.todayInvoices}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </PageSection>
      )}

      {storeId ? (
        <PageSection title="Store KPIs">
          <KpiGrid columns={4}>
            <StatCard title="Today's Invoices" value={stats?.todayInvoices ?? '—'} icon={<ReceiptIcon />} />
            <StatCard
              title="Today's Sales"
              value={stats ? formatCurrency(stats.todaySales, selectedCurrency) : '—'}
              icon={<TrendingUpIcon />}
            />
            <StatCard
              title="Month Sales"
              value={stats ? formatCurrency(stats.monthSales, selectedCurrency) : '—'}
              icon={<TrendingUpIcon />}
            />
            <StatCard title="Products" value={stats?.totalProducts ?? '—'} icon={<ShoppingBagIcon />} />
            <StatCard title="Low Stock" value={stats?.lowStockItems ?? '—'} icon={<InventoryIcon />} />
            <StatCard title="Unread Alerts" value={stats?.unreadNotifications ?? '—'} />
          </KpiGrid>
        </PageSection>
      ) : (
        <Callout variant="info">
          Select a store in the header for store-specific KPIs. Multi-store totals above are never merged across currencies.
        </Callout>
      )}
    </div>
  );
}
